'use client';

import { Icon } from '@/components/ui';
import { cdn } from '@/lib/cdn';
import type { TutorialBannerFragment } from '@/lib/contentful/straps-types';
import { getImageAlt, getImageUrl } from '@/lib/contentful/utils';
import clsx from 'clsx';
import Image from 'next/image';
import { Link } from '@/lib/i18n/navigation';
import styles from './TutorialBanner.module.scss';

export interface TutorialBannerProps {
  /** Contentful TutorialBanner data */
  data: TutorialBannerFragment;
  /** Optional className */
  className?: string;
  /** CMS component index for E2E test targeting */
  cmsIndex?: number;
}

/**
 * Full-width banner with background image/video and CTA
 * Used for tutorials, promotional content, etc.
 */
export function TutorialBanner({ data, className, cmsIndex }: TutorialBannerProps) {
  const { title, subtitle, ctaText, ctaUrl, backgroundImage, backgroundVideo } = data;
  const bgImageSrc = getImageUrl(backgroundImage);
  const bgImageAlt = getImageAlt(backgroundImage);

  // Generate testid prefix for E2E tests
  const testIdPrefix = cmsIndex !== undefined ? `tutorialbanner-${cmsIndex}` : undefined;

  return (
    <section className={clsx(styles.section, className)} data-testid={testIdPrefix}>
      {/* Inner container with background */}
      <div className={styles.container}>
        {/* Background */}
        <div className={styles.background}>
          {backgroundVideo ? (
            <video
              className={styles.video}
              autoPlay
              muted
              loop
              playsInline
              poster={
                bgImageSrc
                  ? cdn.transformImageUrl(bgImageSrc, { width: 1920, quality: 80, format: 'auto' })
                  : undefined
              }
            >
              <source src={cdn.transformVideoUrl(backgroundVideo.url || '')} type="video/mp4" />
            </video>
          ) : bgImageSrc ? (
            <Image
              src={bgImageSrc}
              alt={bgImageAlt}
              fill
              className={styles.image}
              sizes="100vw"
              priority
            />
          ) : null}
          <div className={styles.overlay} />
        </div>

        {/* Content */}
        <div className={styles.content}>
          <div className={styles.textWrapper}>
            {title && (
              <h2
                className={styles.title}
                data-testid={testIdPrefix ? `${testIdPrefix}-title` : undefined}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p
                className={styles.subtitle}
                data-testid={testIdPrefix ? `${testIdPrefix}-subtitle` : undefined}
              >
                {subtitle}
              </p>
            )}
          </div>

          {ctaText && ctaUrl && (
            <Link
              href={ctaUrl}
              className={styles.cta}
              data-testid={testIdPrefix ? `${testIdPrefix}-cta` : undefined}
            >
              <span>{ctaText}</span>
              <Icon name="arrow-right" size={14} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

TutorialBanner.displayName = 'TutorialBanner';
